import React, { useEffect, useId, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { SPECIAL_LABEL_KEY, formatHours, formatPercent, formatWholePercent, isSpecialBucket } from './loadCapacityFormat'

// Geometry (px). The plot keeps a fixed height; columns get an equal share of the available width
// and never shrink under MIN_COLUMN_WIDTH: past that point the chart scrolls horizontally instead.
const LABEL_AREA = 46
const PLOT_HEIGHT = 300
const AXIS_AREA = 44
const LEFT_AXIS = 52
const RIGHT_PAD = 12
const MIN_COLUMN_WIDTH = 56
const BAR_RATIO = 0.62
const Y_DIVISIONS = 4
const DENSE_AXIS_THRESHOLD = 30
// A figure is only written inside a block of the column when the block is tall enough to hold it.
const MIN_INLINE_LABEL_HEIGHT = 18
// "OEE 75 %" needs a wider bar than "75 %": on a dense axis the band keeps its hatch and drops the text.
const MIN_OEE_LABEL_BAR_WIDTH = 44
const SCROLL_PARENT_GUTTER = 24

// The nearest ancestor that scrolls horizontally and has a width of its own (the page container).
const scrollParentOf = (node) => {
  let element = node.parentElement
  while (element) {
    const overflowX = window.getComputedStyle(element).overflowX
    if ((overflowX === 'auto' || overflowX === 'scroll') && element.clientWidth > 0) {
      return element
    }
    element = element.parentElement
  }
  return null
}

// The top of the scale: the largest painted magnitude (load or the capacity the toggle shows),
// rounded up to a round figure so the gridlines read well. Never derived from anything but the
// server's per-column figures (RN44).
const niceCeiling = (value) => {
  if (!(value > 0)) {
    return 100
  }
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)))
  const candidate = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].map((step) => step * magnitude).find((step) => step >= value)
  return candidate ?? value
}

// RN7/RN8/RN15/RN16 -- Jupiter never computes an ISO week, an ISO year or a month: the label is only
// ever derived from what the response carries (`periodKey`, `isoWeek`, `isoYear`, `periodStartDate`),
// exactly as it sent them (S85). `BACKLOG`/`OVERDUE` are translated literals (S86), never the raw code.
export const shortPeriodLabel = (bucket, referenceYear, t_) => {
  const specialLabelKey = SPECIAL_LABEL_KEY[bucket.periodType]
  if (specialLabelKey) {
    return t_(specialLabelKey)
  }
  if (bucket.periodType === 'WEEK' && bucket.isoWeek !== null && bucket.isoWeek !== undefined) {
    const week = t_('W{{week}}', { week: bucket.isoWeek })
    return bucket.isoYear !== referenceYear ? `${week} '${String(bucket.isoYear).slice(-2)}` : week
  }
  return bucket.periodKey
}

// "2026-09-21" -> "21/09": a string rearrangement of the date the server sent, not a date computation.
const dayMonthOf = (isoDate) => {
  if (typeof isoDate !== 'string' || isoDate.length < 10) {
    return ''
  }
  return `${isoDate.slice(8, 10)}/${isoDate.slice(5, 7)}`
}

export const referenceYearOf = (buckets) => {
  const first = buckets.find((bucket) => !isSpecialBucket(bucket))
  if (!first) {
    return null
  }
  return first.periodType === 'WEEK' ? first.isoYear : first.year
}

/**
 * CU8 -- the load-vs-capacity chart, drawn as plain SVG so every element of the design is under
 * control: firm and forecast load stacked, the free capacity as a dim block up to the OEE capacity,
 * the OEE loss hatched above it (optional), the OEE capacity as a line, the excess of a saturated
 * column outlined in red, the current period shaded, the special column (BACKLOG/OVERDUE) first and
 * clipped when it is off the scale, and the load/free figures written over each column. Every number
 * painted here comes straight from `buckets` (RN44): no ISO week, no ratio and no excess is ever
 * computed on the client.
 */
const DrawerLoadCapacityChart = ({ buckets = [], showOeeLoss = true }) => {
  const theme = useTheme()
  const { t_, i18n } = useSmartTranslation()
  const locale = i18n ? i18n.language : undefined
  const patternId = useId()
  const containerRef = useRef(null)
  const [containerWidth, setContainerWidth] = useState(0)

  // The Planner page is sized to its content (MainContainer keeps `minWidth: min-content` so the Gantt
  // can be as wide as it needs), so no ancestor of this box would ever stop it from growing with a
  // 52-column SVG: the whole page would widen instead of the chart scrolling. The box therefore takes
  // the width left to it inside the nearest scrolling ancestor -- the viewport, in practice -- and the
  // SVG scrolls inside it.
  useEffect(() => {
    const measure = () => {
      const box = containerRef.current
      if (!box) {
        return
      }
      const scrollParent = scrollParentOf(box)
      if (!scrollParent) {
        setContainerWidth(box.clientWidth)
        return
      }
      const offset = box.getBoundingClientRect().left - scrollParent.getBoundingClientRect().left + scrollParent.scrollLeft
      setContainerWidth(Math.max(0, Math.floor(scrollParent.clientWidth - offset - SCROLL_PARENT_GUTTER)))
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [buckets.length])

  if (buckets.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary' data-testid='load-capacity-empty'>
        {t_('No data for this plan and these filters')}
      </Typography>
    )
  }

  const columnCount = buckets.length
  const columnWidth = Math.max(MIN_COLUMN_WIDTH, Math.floor((containerWidth - LEFT_AXIS - RIGHT_PAD) / columnCount))
  const width = LEFT_AXIS + columnCount * columnWidth + RIGHT_PAD
  const height = LABEL_AREA + PLOT_HEIGHT + AXIS_AREA
  const barWidth = Math.round(columnWidth * BAR_RATIO)
  const regular = buckets.filter((bucket) => !isSpecialBucket(bucket))
  const scaleSource = regular.length > 0 ? regular : buckets
  const yMax = niceCeiling(Math.max(...scaleSource.map((bucket) => Math.max(
    bucket.totalLoadHours,
    isSpecialBucket(bucket) ? 0 : (showOeeLoss ? bucket.calendarCapacityHours : bucket.oeeCapacityHours)
  ))))
  const y = (value) => LABEL_AREA + PLOT_HEIGHT - (Math.min(Math.max(value, 0), yMax) / yMax) * PLOT_HEIGHT
  const yZero = y(0)
  const referenceYear = referenceYearOf(buckets)
  const labelEvery = columnCount > DENSE_AXIS_THRESHOLD ? 2 : 1

  const colors = {
    firm: theme.palette.success.main,
    forecast: theme.palette.warning.main,
    available: theme.palette.action.hover,
    availableBorder: theme.palette.divider,
    loss: theme.palette.text.disabled,
    capacity: theme.palette.text.primary,
    excess: theme.palette.error.main,
    current: theme.palette.action.selected,
    grid: theme.palette.divider,
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary
  }

  const ticks = Array.from({ length: Y_DIVISIONS + 1 }, (_, index) => (yMax / Y_DIVISIONS) * index)

  return (
    <Box
      ref={containerRef}
      sx={{ width: containerWidth > 0 ? `${containerWidth}px` : '100%', minWidth: 0, maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden' }}
      data-testid='load-capacity-chart-scroll'
    >
      <svg
        width={width}
        height={height}
        role='img'
        aria-label={t_('Load vs capacity')}
        data-testid='load-capacity-chart'
        data-y-max={yMax}
        style={{ display: 'block', fontFamily: theme.typography.fontFamily }}
      >
        <defs>
          <pattern id={patternId} width='8' height='8' patternUnits='userSpaceOnUse' patternTransform='rotate(45)'>
            <line x1='0' y1='0' x2='0' y2='8' stroke={colors.loss} strokeWidth='1.5' opacity='0.55' />
          </pattern>
        </defs>

        {/* horizontal gridlines and the hour scale */}
        {ticks.map((tick) => (
          <g key={`tick-${tick}`}>
            <line x1={LEFT_AXIS} x2={width - RIGHT_PAD} y1={y(tick)} y2={y(tick)} stroke={colors.grid} strokeWidth='1' opacity={tick === 0 ? 1 : 0.5} />
            <text x={LEFT_AXIS - 8} y={y(tick) + 4} textAnchor='end' fontSize='11' fill={colors.muted}>{formatHours(tick, locale)}</text>
          </g>
        ))}
        <text x={LEFT_AXIS - 8} y={LABEL_AREA - 26} textAnchor='end' fontSize='11' fill={colors.muted}>h</text>

        {buckets.map((bucket, index) => {
          const columnX = LEFT_AXIS + index * columnWidth
          const barX = columnX + (columnWidth - barWidth) / 2
          const special = isSpecialBucket(bucket)
          const load = bucket.totalLoadHours
          const firm = bucket.firmLoadHours
          const forecast = bucket.forecastLoadHours
          const oee = special ? 0 : bucket.oeeCapacityHours
          const calendar = special ? 0 : bucket.calendarCapacityHours
          const outOfScale = load > yMax
          const barTop = y(load)
          const shownCapacity = showOeeLoss ? calendar : oee
          const labelBaseline = Math.min(barTop, special ? barTop : y(shownCapacity)) - 8
          const isPeriodLabelShown = special || bucket.current || index % labelEvery === 0
          // S89/INV-5 -- a null ratio is read out as "Not available", never as "0 %".
          const ratioLabel = bucket.utilizationRatioPercent === null || bucket.utilizationRatioPercent === undefined
            ? t_('Not available')
            : `${formatPercent(bucket.utilizationRatioPercent, locale)} %`
          const currentLabelKey = bucket.periodType === 'MONTH' ? 'Current month' : 'Current week'

          return (
            <g
              key={bucket.periodKey}
              data-testid={`load-capacity-column-${bucket.periodKey}`}
              data-period-type={bucket.periodType}
              data-saturated={bucket.saturated}
              data-current={bucket.current}
              data-special={special}
            >
              <title>{`${t_('Load vs capacity')}: ${ratioLabel}`}</title>

              {/* S87 -- the signal is decided solely by `current === true`, never by comparing dates. */}
              {bucket.current
                ? (
                  <g data-testid='load-capacity-current-period' data-period-key={bucket.periodKey}>
                    <rect x={columnX} y={0} width={columnWidth} height={LABEL_AREA + PLOT_HEIGHT} fill={colors.current} stroke={colors.firm} strokeWidth='1' />
                    <text x={columnX + 6} y={14} fontSize='11' fontWeight='600' fill={colors.text}>{t_(currentLabelKey)}</text>
                  </g>
                  )
                : null}

              {/* free capacity, up to the OEE capacity; BACKLOG/OVERDUE never draw it (S86) */}
              {!special && oee > load
                ? (
                  <rect
                    x={barX} y={y(oee)} width={barWidth} height={y(load) - y(oee)}
                    fill={colors.available} stroke={colors.availableBorder} strokeWidth='1' strokeDasharray='3 3'
                    data-testid={`load-capacity-column-${bucket.periodKey}-available`}
                    data-hours={Math.max(bucket.availableCapacityHours, 0)}
                  />
                  )
                : null}

              {/* OEE loss: the hatched band between the OEE capacity and the calendar capacity */}
              {!special && showOeeLoss && calendar > oee
                ? (
                  <rect
                    x={barX} y={y(calendar)} width={barWidth} height={y(oee) - y(calendar)}
                    fill={`url(#${patternId})`} stroke={colors.availableBorder} strokeWidth='1'
                    data-testid={`load-capacity-column-${bucket.periodKey}-oee-loss`}
                    data-hours={calendar - oee}
                  />
                  )
                : null}

              {/* the stacked load: firm below, forecast above (INV-3) */}
              {firm > 0
                ? (
                  <rect
                    x={barX} y={y(firm)} width={barWidth} height={yZero - y(firm)} fill={colors.firm}
                    data-testid={`load-capacity-column-${bucket.periodKey}-firm`} data-hours={firm}
                  />
                  )
                : null}
              {forecast > 0
                ? (
                  <rect
                    x={barX} y={y(firm + forecast)} width={barWidth} height={y(firm) - y(firm + forecast)} fill={colors.forecast}
                    data-testid={`load-capacity-column-${bucket.periodKey}-forecast`} data-hours={forecast}
                  />
                  )
                : null}

              {/* an off-scale special column is cut at the top of the scale */}
              {outOfScale
                ? (
                  <g data-testid={`load-capacity-column-${bucket.periodKey}-out-of-scale`}>
                    <line x1={barX - 2} y1={LABEL_AREA + 10} x2={barX + barWidth + 2} y2={LABEL_AREA + 4} stroke={theme.palette.background.paper} strokeWidth='3' />
                    <line x1={barX - 2} y1={LABEL_AREA + 16} x2={barX + barWidth + 2} y2={LABEL_AREA + 10} stroke={theme.palette.background.paper} strokeWidth='3' />
                  </g>
                  )
                : null}

              {/* S88 -- the excess is only ever outlined on `saturated === true` columns */}
              {!special && bucket.saturated && load > oee
                ? (
                  <rect
                    x={barX - 1} y={y(load) - 1} width={barWidth + 2} height={y(oee) - y(load) + 1}
                    fill='none' stroke={colors.excess} strokeWidth='1.5'
                    data-testid={`load-capacity-column-${bucket.periodKey}-excess`}
                  />
                  )
                : null}

              {/* the utilisation of the column (the server's ratio, RN44), written inside the bar when it fits */}
              {!special && !outOfScale && bucket.utilizationRatioPercent !== null && bucket.utilizationRatioPercent !== undefined && (yZero - barTop) >= MIN_INLINE_LABEL_HEIGHT
                ? (
                  <text
                    x={columnX + columnWidth / 2} y={(barTop + yZero) / 2 + 4} textAnchor='middle' fontSize='11' fontWeight='700'
                    fill={theme.palette.common.white} opacity='0.92'
                    data-testid={`load-capacity-column-${bucket.periodKey}-ratio-label`}
                  >
                    {`${formatWholePercent(bucket.utilizationRatioPercent, locale)} %`}
                  </text>
                  )
                : null}

              {/* the OEE share of the calendar, written in the hatched band when it fits */}
              {!special && showOeeLoss && calendar > oee && (y(oee) - y(calendar)) >= MIN_INLINE_LABEL_HEIGHT && barWidth >= MIN_OEE_LABEL_BAR_WIDTH
                ? (
                  <text
                    x={columnX + columnWidth / 2} y={(y(calendar) + y(oee)) / 2 + 4} textAnchor='middle' fontSize='10' fill={colors.muted}
                    data-testid={`load-capacity-column-${bucket.periodKey}-oee-label`}
                  >
                    {t_('OEE {{percent}} %', { percent: formatWholePercent((oee / calendar) * 100, locale) })}
                  </text>
                  )
                : null}

              {/* S83/S86 -- the OEE capacity line, never drawn over BACKLOG/OVERDUE */}
              {!special
                ? (
                  <line
                    x1={barX - 5} x2={barX + barWidth + 5} y1={y(oee)} y2={y(oee)} stroke={colors.capacity} strokeWidth='2'
                    data-testid={`load-capacity-column-${bucket.periodKey}-capacity`} data-hours={oee}
                  >
                    <title>{t_('OEE capacity (calendar × OEE)')}</title>
                  </line>
                  )
                : null}

              {/* the figures over the column: load, then free hours or the excess (RN28) */}
              <text
                x={columnX + columnWidth / 2} y={outOfScale ? LABEL_AREA - 24 : labelBaseline - 13} textAnchor='middle' fontSize='12' fontWeight='700'
                fill={special ? colors.excess : colors.text}
                data-testid={`load-capacity-column-${bucket.periodKey}-load-label`}
              >
                {`${formatHours(load, locale)} h`}
              </text>
              {outOfScale
                ? (
                  <text x={columnX + columnWidth / 2} y={LABEL_AREA - 11} textAnchor='middle' fontSize='10' fill={colors.muted}>
                    {t_('Out of scale')}
                  </text>
                  )
                : null}
              {!special && !outOfScale
                ? (bucket.saturated
                    ? (
                      <text
                        x={columnX + columnWidth / 2} y={labelBaseline} textAnchor='middle' fontSize='11' fontWeight='700' fill={colors.excess}
                        data-testid={`load-capacity-column-${bucket.periodKey}-excess-label`}
                      >
                        {t_('+{{hours}} h', { hours: formatHours(bucket.excessHours, locale) })}
                      </text>
                      )
                    : (
                      <text
                        x={columnX + columnWidth / 2} y={labelBaseline} textAnchor='middle' fontSize='11' fill={colors.muted}
                        data-testid={`load-capacity-column-${bucket.periodKey}-free-label`}
                      >
                        {t_('{{hours}} free', { hours: formatHours(Math.max(bucket.availableCapacityHours, 0), locale) })}
                      </text>
                      ))
                : null}

              {/* the period label under the axis; every other one when the axis gets dense */}
              {isPeriodLabelShown
                ? (
                  <g data-testid={`load-capacity-axis-label-${bucket.periodKey}`}>
                    <text
                      x={columnX + columnWidth / 2} y={yZero + 18} textAnchor='middle' fontSize='12'
                      fontWeight={bucket.current || special ? '700' : '500'}
                      fill={special ? colors.excess : (bucket.current ? colors.firm : colors.text)}
                    >
                      {shortPeriodLabel(bucket, referenceYear, t_)}
                    </text>
                    {!special && bucket.periodType === 'WEEK'
                      ? (
                        <text x={columnX + columnWidth / 2} y={yZero + 33} textAnchor='middle' fontSize='10' fill={colors.muted}>
                          {dayMonthOf(bucket.periodStartDate)}
                        </text>
                        )
                      : null}
                  </g>
                  )
                : null}
            </g>
          )
        })}
      </svg>
    </Box>
  )
}

// Story 177 (AP12), D-P (a) -- the look of the "Load and capacity" block, validated by the PO in the preview.
// It is drawn in a fixed coordinate system that scales to the width of the block (`viewBox`), so every column
// of the chosen range is always inside the block: no minimum column width, no horizontal scroll and no way
// to widen the page (RN61, H153 S85). The drawer of story 175 never asks for it (it keeps the chart above).
const BLOCK_VIEW_WIDTH = 1200
const BLOCK_VIEW_HEIGHT = 360
const BLOCK_LEFT = 44
const BLOCK_RIGHT = 8
const BLOCK_TOP = 34
const BLOCK_BOTTOM = 48
const BLOCK_BOTTOM_TILTED = 66
const BLOCK_MAX_BAR_WIDTH = 70
// RN61 -- up to this many bars the figures carry their unit and the band says "{n} h OEE"; above it the bar
// writes only the number, the band drops its text and the period labels tilt. Counted over the regular bars,
// the way the validated preview counts them.
const BLOCK_LABELLED_BARS = 26
const BLOCK_TILT_DEGREES = -45

const wholeHours = (value) => Math.round(Number(value) || 0)

const LoadCapacityBlockChart = ({ buckets = [], showOeeLoss = true }) => {
  const theme = useTheme()
  const { t_, i18n } = useSmartTranslation()
  const locale = i18n ? i18n.language : undefined
  const patternId = useId()

  if (buckets.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary' data-testid='load-capacity-empty'>
        {t_('No data for this plan and these filters')}
      </Typography>
    )
  }

  const columnCount = buckets.length
  const regular = buckets.filter((bucket) => !isSpecialBucket(bucket))
  const tilted = regular.length > BLOCK_LABELLED_BARS
  const writesUnits = !tilted
  const bottom = tilted ? BLOCK_BOTTOM_TILTED : BLOCK_BOTTOM
  const plotHeight = BLOCK_VIEW_HEIGHT - BLOCK_TOP - bottom
  const slot = (BLOCK_VIEW_WIDTH - BLOCK_LEFT - BLOCK_RIGHT) / columnCount
  const barWidth = Math.min(slot * 0.62, BLOCK_MAX_BAR_WIDTH)
  const scaleSource = regular.length > 0 ? regular : buckets
  const yMax = niceCeiling(Math.max(...scaleSource.map((bucket) => Math.max(
    bucket.totalLoadHours,
    isSpecialBucket(bucket) ? 0 : (showOeeLoss ? bucket.calendarCapacityHours : bucket.oeeCapacityHours)
  ))))
  const y = (value) => BLOCK_TOP + plotHeight - (Math.min(Math.max(value, 0), yMax) / yMax) * plotHeight
  const yZero = y(0)
  const referenceYear = referenceYearOf(buckets)
  const labelEvery = columnCount > DENSE_AXIS_THRESHOLD ? 2 : 1

  const colors = {
    firm: theme.palette.success.main,
    forecast: theme.palette.warning.main,
    available: theme.palette.action.hover,
    availableBorder: theme.palette.divider,
    loss: theme.palette.text.disabled,
    capacity: theme.palette.text.primary,
    excess: theme.palette.error.main,
    current: theme.palette.action.selected,
    grid: theme.palette.divider,
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary
  }
  const ticks = Array.from({ length: Y_DIVISIONS + 1 }, (_, index) => (yMax / Y_DIVISIONS) * index)

  return (
    <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }} data-testid='load-capacity-chart-fit'>
      <svg
        viewBox={`0 0 ${BLOCK_VIEW_WIDTH} ${BLOCK_VIEW_HEIGHT}`}
        width='100%'
        role='img'
        aria-label={t_('Load vs capacity')}
        data-testid='load-capacity-chart'
        data-y-max={yMax}
        data-view-width={BLOCK_VIEW_WIDTH}
        data-look='block'
        style={{ display: 'block', fontFamily: theme.typography.fontFamily }}
      >
        <defs>
          <pattern id={patternId} width='8' height='8' patternUnits='userSpaceOnUse' patternTransform='rotate(45)'>
            <line x1='0' y1='0' x2='0' y2='8' stroke={colors.loss} strokeWidth='1.5' opacity='0.55' />
          </pattern>
        </defs>

        {ticks.map((tick) => (
          <g key={`tick-${tick}`}>
            <line x1={BLOCK_LEFT} x2={BLOCK_VIEW_WIDTH - BLOCK_RIGHT} y1={y(tick)} y2={y(tick)} stroke={colors.grid} strokeWidth='1' opacity={tick === 0 ? 1 : 0.5} />
            <text x={BLOCK_LEFT - 8} y={y(tick) + 4} textAnchor='end' fontSize='11' fill={colors.muted}>{formatHours(tick, locale)}</text>
          </g>
        ))}
        <text x={BLOCK_LEFT - 8} y={BLOCK_TOP - 14} textAnchor='end' fontSize='11' fill={colors.muted}>h</text>

        {buckets.map((bucket, index) => {
          const columnX = BLOCK_LEFT + index * slot
          const centerX = columnX + slot / 2
          const barX = centerX - barWidth / 2
          const special = isSpecialBucket(bucket)
          const load = bucket.totalLoadHours
          const firm = bucket.firmLoadHours
          const forecast = bucket.forecastLoadHours
          const oee = special ? 0 : bucket.oeeCapacityHours
          const calendar = special ? 0 : bucket.calendarCapacityHours
          const outOfScale = load > yMax
          const isPeriodLabelShown = special || bucket.current || index % labelEvery === 0
          const ratioLabel = bucket.utilizationRatioPercent === null || bucket.utilizationRatioPercent === undefined
            ? t_('Not available')
            : `${formatPercent(bucket.utilizationRatioPercent, locale)} %`
          const currentLabelKey = bucket.periodType === 'MONTH' ? 'Current month' : 'Current week'
          const axisY = yZero + 16
          const barLabelY = Math.min(y(Math.min(load, yMax) / 2) + 4, yZero - 6)

          return (
            <g
              key={bucket.periodKey}
              data-testid={`load-capacity-column-${bucket.periodKey}`}
              data-period-type={bucket.periodType}
              data-saturated={bucket.saturated}
              data-current={bucket.current}
              data-special={special}
            >
              <title>{`${t_('Load vs capacity')}: ${ratioLabel}`}</title>

              {bucket.current
                ? (
                  <g data-testid='load-capacity-current-period' data-period-key={bucket.periodKey}>
                    <rect x={columnX} y={0} width={slot} height={BLOCK_TOP + plotHeight} fill={colors.current} stroke={colors.firm} strokeWidth='1' />
                    <text x={columnX + 4} y={14} fontSize='11' fontWeight='600' fill={colors.text}>{t_(currentLabelKey)}</text>
                  </g>
                  )
                : null}

              {!special && oee > load
                ? (
                  <rect
                    x={barX} y={y(oee)} width={barWidth} height={y(load) - y(oee)}
                    fill={colors.available} stroke={colors.availableBorder} strokeWidth='1' strokeDasharray='3 3'
                    data-testid={`load-capacity-column-${bucket.periodKey}-available`}
                    data-hours={Math.max(bucket.availableCapacityHours, 0)}
                  />
                  )
                : null}

              {!special && showOeeLoss && calendar > oee
                ? (
                  <rect
                    x={barX} y={y(calendar)} width={barWidth} height={y(oee) - y(calendar)}
                    fill={`url(#${patternId})`} stroke={colors.availableBorder} strokeWidth='1'
                    data-testid={`load-capacity-column-${bucket.periodKey}-oee-loss`}
                    data-hours={calendar - oee}
                  />
                  )
                : null}

              {firm > 0
                ? (
                  <rect
                    x={barX} y={y(firm)} width={barWidth} height={yZero - y(firm)} fill={colors.firm}
                    data-testid={`load-capacity-column-${bucket.periodKey}-firm`} data-hours={firm}
                  />
                  )
                : null}
              {forecast > 0
                ? (
                  <rect
                    x={barX} y={y(firm + forecast)} width={barWidth} height={y(firm) - y(firm + forecast)} fill={colors.forecast}
                    data-testid={`load-capacity-column-${bucket.periodKey}-forecast`} data-hours={forecast}
                  />
                  )
                : null}

              {outOfScale
                ? (
                  <g data-testid={`load-capacity-column-${bucket.periodKey}-out-of-scale`}>
                    <line x1={barX - 2} y1={BLOCK_TOP + 10} x2={barX + barWidth + 2} y2={BLOCK_TOP + 4} stroke={theme.palette.background.paper} strokeWidth='3' />
                    <line x1={barX - 2} y1={BLOCK_TOP + 16} x2={barX + barWidth + 2} y2={BLOCK_TOP + 10} stroke={theme.palette.background.paper} strokeWidth='3' />
                    <text x={centerX} y={BLOCK_TOP - 4} textAnchor='middle' fontSize='10' fill={colors.muted}>{t_('Out of scale')}</text>
                  </g>
                  )
                : null}

              {!special && bucket.saturated && load > oee
                ? (
                  <rect
                    x={barX - 1} y={y(load) - 1} width={barWidth + 2} height={y(oee) - y(load) + 1}
                    fill='none' stroke={colors.excess} strokeWidth='1.5'
                    data-testid={`load-capacity-column-${bucket.periodKey}-excess`}
                  />
                  )
                : null}

              {/* H153 S89 (amended) -- the hours of the OEE loss, written in the hatched band */}
              {!special && showOeeLoss && calendar > oee && writesUnits
                ? (
                  <text
                    x={centerX} y={(y(calendar) + y(oee)) / 2 + 4} textAnchor='middle' fontSize='11' fill={colors.text}
                    data-testid={`load-capacity-column-${bucket.periodKey}-oee-label`}
                  >
                    {t_('{{hours}} h OEE', { hours: formatHours(wholeHours(Math.max(calendar - oee, 0)), locale) })}
                  </text>
                  )
                : null}

              {!special
                ? (
                  <line
                    x1={barX - 4} x2={barX + barWidth + 4} y1={y(oee)} y2={y(oee)} stroke={colors.capacity} strokeWidth='2'
                    data-testid={`load-capacity-column-${bucket.periodKey}-capacity`} data-hours={oee}
                  >
                    <title>{t_('OEE capacity (calendar × OEE)')}</title>
                  </line>
                  )
                : null}

              {/* H153 S83/S88 (amended) -- the one figure of the bar: its total load, in whole hours */}
              {load > 0
                ? (
                  <text
                    x={centerX} y={barLabelY} textAnchor='middle' fontSize={tilted ? 10 : 11} fontWeight='700'
                    fill={theme.palette.common.white}
                    data-testid={`load-capacity-column-${bucket.periodKey}-load-label`}
                  >
                    {writesUnits
                      ? t_('{{hours}} h', { hours: formatHours(wholeHours(load), locale) })
                      : formatHours(wholeHours(load), locale)}
                  </text>
                  )
                : null}

              {isPeriodLabelShown
                ? (
                  <g data-testid={`load-capacity-axis-label-${bucket.periodKey}`}>
                    <text
                      x={tilted ? centerX + 4 : centerX}
                      y={axisY}
                      textAnchor={tilted ? 'end' : 'middle'}
                      transform={tilted ? `rotate(${BLOCK_TILT_DEGREES} ${centerX + 4} ${axisY})` : undefined}
                      data-tilt={tilted ? BLOCK_TILT_DEGREES : 0}
                      fontSize='11'
                      fontWeight={bucket.current || special ? '700' : '500'}
                      fill={special ? colors.excess : (bucket.current ? colors.firm : colors.text)}
                    >
                      {shortPeriodLabel(bucket, referenceYear, t_)}
                    </text>
                    {!tilted && !special && bucket.periodType === 'WEEK'
                      ? (
                        <text x={centerX} y={axisY + 14} textAnchor='middle' fontSize='10' fill={colors.muted}>
                          {dayMonthOf(bucket.periodStartDate)}
                        </text>
                        )
                      : null}
                  </g>
                  )
                : null}
            </g>
          )
        })}
      </svg>
    </Box>
  )
}

/**
 * The load-vs-capacity chart. By default it is the chart of the machine detail drawer (story 175), exactly as it
 * has always been; `variant='block'` is the look story 177 gives the "Load and capacity" block.
 */
const LoadCapacityChart = ({ variant = 'drawer', ...props }) => (
  variant === 'block' ? <LoadCapacityBlockChart {...props} /> : <DrawerLoadCapacityChart {...props} />
)

export default LoadCapacityChart
